import {
  updatePartnerAccreditationFlags,
  updatePartnerData,
  updatePartnerProgramFlags,
} from 'legacy/lut/features/partner/partnerSlice'
import { memo, useCallback, useState } from 'react'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { ModalDialog } from 'core-components/library/modalDialog'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { PartnerEditorFormKeys } from './configs'
import { useHasChanges } from './hooks/useHasChanges'
import { IPartnerEditorProps, IPartnerEditorSettings } from './models'
import { PartnerEditorContent } from './PartnerEditorContent'
import { partnerEditorTranslation } from './translation'

import styles from './PartnerEditor.module.scss'

export const PartnerEditor = memo<IPartnerEditorProps>(
  ({
    onClose,
    partner,
    partnerFlags,
    partnerAttachments,
    isVisible,
    isReadOnly,
    onPartnerUpdate,
    onPartnerFlagsUpdate,
    onPartnerAttachmentsUpdate,
  }) => {
    const dispatch = useAppDispatch()
    const [isCloseConfirmDialogVisible, setIsCloseConfirmDialogVisible] = useState(false)
    const [settingsFormData] = useGetFormData<IPartnerEditorSettings>(PartnerEditorFormKeys.Setting)

    const hasModifiedValues = useHasChanges()

    const handleOnConfirmClose = useCallback(() => {
      if (settingsFormData?.formData?.actualData) {
        dispatch(
          updatePartnerData({
            data: settingsFormData.formData.actualData.partner,
            status: RequestStatus.FULFILLED,
            message: '',
          })
        )

        dispatch(
          updatePartnerProgramFlags(settingsFormData.formData.actualData.partnerFlags.program)
        )

        dispatch(
          updatePartnerAccreditationFlags(
            settingsFormData.formData.actualData.partnerFlags.accreditation
          )
        )
      }
      Object.values(PartnerEditorFormKeys).forEach((key) => {
        dispatch(resetAllModifiedDataByKey(key))
      })

      if (onPartnerUpdate) {
        onPartnerUpdate(settingsFormData.formData.actualData.partner)
      }
      if (onPartnerFlagsUpdate) {
        onPartnerFlagsUpdate(settingsFormData.formData.actualData.partnerFlags)
      }
      if (onPartnerAttachmentsUpdate) {
        onPartnerAttachmentsUpdate(settingsFormData.formData.actualData.partnerAttachments)
      }

      if (onClose) {
        onClose()
      }

      setIsCloseConfirmDialogVisible(false)
    }, [
      dispatch,
      onClose,
      onPartnerAttachmentsUpdate,
      onPartnerFlagsUpdate,
      onPartnerUpdate,
      settingsFormData.formData.actualData,
    ])

    const handleOnClose = useCallback(() => {
      if (hasModifiedValues) {
        setIsCloseConfirmDialogVisible(true)
      } else {
        handleOnConfirmClose()
      }
    }, [handleOnConfirmClose, hasModifiedValues])

    const handleOnRejectClose = useCallback(() => {
      setIsCloseConfirmDialogVisible(false)
    }, [])

    /** ---------------------- Render content ------------------------ */

    const renderContent = useCallback(
      () => (
        <RenderWhen condition={Boolean(partner && partnerAttachments && partnerFlags)}>
          <PartnerEditorContent
            partner={partner}
            partnerAttachments={partnerAttachments}
            partnerFlags={partnerFlags}
            isReadOnly={isReadOnly}
          />
        </RenderWhen>
      ),
      [isReadOnly, partner, partnerAttachments, partnerFlags]
    )

    /** ---------------------- Render ------------------------ */

    if (!isVisible || partner === null || partnerFlags === null || partnerAttachments === null) {
      return null
    }

    return (
      <>
        <ModalDialog
          classes={styles}
          show={isVisible}
          content={renderContent}
          onClose={handleOnClose}
          closeOnOutsideClick={false}
          size="xl"
        />
        <ConfirmDialog
          content={partnerEditorTranslation.dialog.closeConfirm.content}
          confirmBtnText={partnerEditorTranslation.dialog.closeConfirm.confirmBtn}
          onConfirm={handleOnConfirmClose}
          isVisible={isCloseConfirmDialogVisible}
          title={partnerEditorTranslation.dialog.closeConfirm.title}
          onReject={handleOnRejectClose}
          rejectBtnText={partnerEditorTranslation.dialog.closeConfirm.rejectBtn}
          size="xs"
        />
      </>
    )
  }
)

PartnerEditor.displayName = 'PartnerEditor'
